import { DndContext, DragOverlay, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useQueryClient } from "@tanstack/react-query";
import i18next, { t } from "i18next";
import { isEqual } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import arrowIcon from "../../assets/images/chevron-left.svg";
import { GeneralButton } from "../../components";
import { EndPoints } from "../../Constants/endpoints";
import {
  setMedicalRecord,
  setMedicalTemplate,
} from "../../redux/reducers/medicalRecordSlice";
import { RootState } from "../../redux/store";
import { useToast } from "../../Services/ToastContext";
import CancelMedicalRecord from "./component/CancelMedicalRecordModal/CancelMedicalRecordModal";
import CustomizeButton from "./component/CustomizeButton/CustomizeButton";
import { FieldRenderer } from "./component/FieldRenderer/FieldRenderer";
import PatientInfo from "./component/PatientInfo/PatientInfo";
import { useMedicalRecordData } from "./hooks/useMedicalRecordData";
import { useMedicalRecordNavigation } from "./hooks/useMedicalRecordNavigation";
import { useMedicalTemplateData } from "./hooks/useMedicalTemplateData";
import MedicalRecordHeader from "./MedicalRecordHeader/MedicalRecordHeader";
import SaveRecordAndTemplateConfirm from "./MedicalRecordTemplates/SaveRecordAndTEmplateConfirm/SaveRecordAndTEmplateConfirm";
import TemplateNameModal from "./MedicalRecordTemplates/TemplateNameModal";
import MultiStepProgress from "./MultiStepProgress";
import PreviewMedicalRecord from "./PreviewMedicalRecord/PreviewMedicalRecord";
import SortableField from "./component/SortableFields/SortableField";
import SortableSubStepField from "./component/SortableFields/SortableSubStepField";
import { useSortableFields } from "./component/SortableFields/useSortableFields ";
import "../../assets/css/medical.css";
import {
  cleanEmptyStrings,
  filterByVisibleKeys,
  getFieldsHasKeys,
  getFirstVisibleSubStepIndex,
  getNextVisibleSubStepIndex,
  getPrevVisibleSubStepIndex,
  LoadingFieldsSkeleton,
  logFieldOrder,
  stringifyValues,
  updatedSteps,
} from "./utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildFinalSchema, getStepFieldNames } from "./schemas";
import { useTranslation } from "react-i18next";

const MedicalRecord: React.FC = () => {
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState<boolean>(false);
  const [isAskToSaveTemplateOpen, setIsAskToSaveTemplateOpen] =
    useState<boolean>(false);
  const [saveTemporaryAndContinue, setSaveTemporaryAndContinue] =
    useState<boolean>(false);
  const [updatedTemplate, setUpdatedTemplate] = useState({});
  const [recordFormData, setRecordFormData] = useState<any>();
  const [localSteps, setLocalSteps] = useState<any[]>([]);
  const [saveOverOldTemplate, setSaveOverOldTemplate] =
    useState<boolean>(false);

  const {
    currentStep,
    currentSubStep,
    prevStep,
    setCurrentStep,
    setCurrentSubStep,
    setPrevStep,
    currentStepData,
    currentSubStepData,
    resetSteps,
    isCustomizeMode,
    isViewMode,
    isUpdateRecordMode,
    isLastStep,
    stepFieldIds,
    subStepFieldIds,
  } = useMedicalRecordNavigation(localSteps);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { PatientId, templateId, type } = useParams();

  const medicalRecordTemplate = useSelector(
    (state: RootState) => state.medicalRecord.template,
  );

  const medicalRecord: any = useSelector(
    (state: RootState) => state.medicalRecord.record,
  );

  const dispatch = useDispatch();
  const location = useLocation();
  const validationSchema = useMemo(() => {
    return buildFinalSchema(localSteps, medicalRecord, t);
  }, [localSteps, medicalRecord, i18n.language]);



  const methods = useForm<any>({
    mode: "onChange",
    resolver:
      validationSchema && type !== "details" && !isCustomizeMode
        ? zodResolver(validationSchema)
        : undefined,
    shouldUnregister: false,

    defaultValues: { Attributes: {}, DateOfPregnancy: "" },
  });

  const { handleSubmit, control, trigger, watch, formState } = methods;


  const values = useWatch({ control });
  const prev = useRef(values);

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const {
    UpdateMedicalTemplateMutation,
    getMedicalTemplateQuery,
    selectedTemplate,
    isDefaultTemplate,
    totalVisibleKeys
  } = useMedicalTemplateData({
    saveTemporaryAndContinue: saveTemporaryAndContinue,
    setLocalSteps: setLocalSteps,
    methods: methods,
  });

  const { updateOrCreateRecord, createRecordMutation } = useMedicalRecordData({
    resetSteps: resetSteps,
    methods: methods,
  });
  const handleCustomizeTemplate = (values: any) => {
    const updatedTemplate: any = {
      ...medicalRecordTemplate,
      MedicalHistoryTypeId: templateId,
      Steps: updatedSteps(localSteps, values),
    };

    dispatch(setMedicalTemplate(updatedTemplate));
    setUpdatedTemplate(updatedTemplate);

    if (!saveTemporaryAndContinue) {
      !saveOverOldTemplate
        ? setIsSaveTemplateOpen(true)
        : UpdateMedicalTemplateMutation.mutate(updatedTemplate, {
          onSuccess: () => {
            showToast(t("success"), "success");
            queryClient.refetchQueries({
              queryKey: [EndPoints.MedicalHistoryTypes],
              exact: false,
            });

            PatientId
              ? navigate(`/patients_list/${PatientId}/medical-records`)
              : handleBack();
          },
          onError: (error: any) =>
            showToast(error.message || t("failed"), "error"),
        });
    } else {
      handleBack();
    }
  };

  const onSubmit = (values: any) => {


    const submitValues: any = filterByVisibleKeys(
      values,
      totalVisibleKeys
    );
    const cleanedValues = cleanEmptyStrings(submitValues);
    const { Attributes, ...rest } = cleanedValues;

    if (isCustomizeMode) {
      handleCustomizeTemplate(cleanEmptyStrings(values));
    } else {
      const fieldMap = getFieldsHasKeys(localSteps);
      let keyToAttributes = {};
      fieldMap.forEach((element) => {
        const fieldValue = watch(element?.keyName);

        if (
          fieldValue && fieldValue !== "" && totalVisibleKeys?.includes(element?.keyName) &&
          !Array.isArray(fieldValue) &&
          typeof fieldValue !== "object"
        ) {
          keyToAttributes = {
            ...keyToAttributes,
            [element?.attrName]: `${fieldValue}`,
          };
        }
      });

      const cleanedAttributes = Object.fromEntries(
        Object.entries({ ...Attributes, ...keyToAttributes }).filter(
          ([_, value]) =>

            value !== null &&
            value !== undefined &&
            !(typeof value === "string" && value.trim() === "")
        )
      );

      const bodyData = {
        PatientId,
        MedicalHistoryTypeId: medicalRecordTemplate?.Id,
        ...rest,
        MedicalHistoryDate:
          values?.MedicalHistoryDate ??
          moment().locale("en").format("DD-MM-YYYY HH:mm:ss"),
        // Attributes: a,
        Attributes: stringifyValues(cleanedAttributes),
      };
      dispatch(setMedicalRecord(bodyData));

      if (saveTemporaryAndContinue) {
        setIsAskToSaveTemplateOpen(true);
        setRecordFormData(bodyData);
      } else {
        navigate(`Preview`);
      }
    }
  };

  const isCurrentStepVisible =
    !!currentStepData && methods.watch(`id_${currentStepData?.Id}`)?.IsVisible;

  const { sensors, handleDragStart, handleDragEnd, activeField } =
    useSortableFields({
      localSteps,
      currentStep,
      currentSubStep,
      setLocalSteps,
    });

  const handleFinish = async () => {
    const values = methods.getValues();
    const isValid = await trigger();

    if (!isValid) {
      const errors: any =
        typeof formState?.errors !== "object" ? formState.errors : [];

      const messages = errors?.map((item: any) => item?.message);

      showToast(
        errors.length && errors
          ? messages?.join(", ")
          : t("please_fill_all_required_fields"),
        "error",
      );
      return;
    }

    onSubmit(values);
  };

  const watchedValues = useWatch({ control });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      methods.trigger();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [watchedValues, methods]);

  const recordButtons = (
    <div className="col-span-2 flex justify-between gap-2 py-5">
      <div className="flex gap-2">
        <CancelMedicalRecord
          onClick={() => {
            PatientId
              ? navigate(`/patient/${PatientId}/add-medical-record`)
              : handleBack();
            dispatch(setMedicalRecord(null));
          }}
        />
        {!isViewMode && !isLastStep && (
          <GeneralButton
            isLoading={createRecordMutation?.isPending}
            type="submit"
            className={"w-fit flex-row-reverse border border-primary !px-8"}
            title={t("finish")}
            onClick={handleFinish}
            color="lite"
          />
        )}
      </div>
      <div className="flex gap-2">
        {!(currentStep == 0 && currentSubStep == 0) && (
          <GeneralButton
            className={"w-fit flex-row-reverse border border-primary !pe-6"}
            title={
              <div className="flex items-center justify-start gap-2">
                <img
                  className={i18next?.language == "ar" ? "" : "rotate-180"}
                  alt="back"
                  src={arrowIcon}
                />
                <p>{t("back")}</p>
              </div>
            }
            color="lite"
            onClick={async () => {
              const fieldNames = getStepFieldNames(
                currentStepData,
                methods.getValues(),
              );
              const isValid = await trigger(fieldNames);
              if (!isValid) return;

              const subSteps = currentStepData?.InverseParentSteps ?? [];
              const prevVisibleSubStep = getPrevVisibleSubStepIndex(subSteps, currentSubStep);

              if (prevVisibleSubStep !== -1) {
                // there's a visible substep before this one within the same step
                setCurrentSubStep(prevVisibleSubStep);
              } else if (currentStep > 0) {
                // no visible substep left behind us — go back a step
                setCurrentStep((prev) => prev - 1);
                const prevStepSubSteps =
                  localSteps?.[currentStep - 1]?.InverseParentSteps ?? [];
                setCurrentSubStep(getFirstVisibleSubStepIndex(prevStepSubSteps));
              }
            }}
          />
        )}
        {!isLastStep && (
          <GeneralButton
            className={"!w-fit flex-row-reverse border border-primary !px-6"}
            title={
              <div className="flex items-center gap-2">
                <p>{t("next")}</p>
                <img
                  className={`brightness-[200] ${i18next?.language != "ar" ? "" : "rotate-180"}`}
                  alt="next"
                  src={arrowIcon}
                />
              </div>
            }
            onClick={async () => {
              const fieldNames = getStepFieldNames(
                currentStepData,
                methods.getValues(),
              );

              const isValid = await trigger(fieldNames);
              if (!isValid) return;

              const subSteps = currentStepData?.InverseParentSteps ?? [];
              const nextVisibleSubStep = getNextVisibleSubStepIndex(subSteps, currentSubStep);

              if (nextVisibleSubStep !== -1) {
                setCurrentSubStep(nextVisibleSubStep);
              } else {
                setCurrentStep((prev: number) => {
                  setPrevStep(prev as any);
                  return prev + 1;
                });

                const nextStepSubSteps =
                  localSteps?.[currentStep + 1]?.InverseParentSteps ?? [];
                setCurrentSubStep(getFirstVisibleSubStepIndex(nextStepSubSteps));
              }
            }}
          />
        )}
        {isLastStep && !isViewMode && (
          <GeneralButton
            isLoading={createRecordMutation?.isPending}
            type="submit"
            className={"w-fit flex-row-reverse border border-primary !px-8"}
            title={t("finish")}
            onClick={handleFinish}
            color="lite"
          />
        )}
      </div>
    </div>
  );

  const customizeButtons = (
    <div className="col-span-2 flex justify-end gap-2 py-5">
      <GeneralButton
        color="lite"
        onClick={() => handleBack()}
        title={t("discard_changes")}
        className={"!text-primary-text !bg-white"}
      />

      <GeneralButton
        isLoading={createRecordMutation?.isPending}
        type="submit"
        color={!isDefaultTemplate ? "lite" : undefined}
        onClick={() => {
          setSaveTemporaryAndContinue(false);
          setSaveOverOldTemplate(false);
        }}
        className="!h-fit !min-w-28 !px-5"
        title={
          isDefaultTemplate ? t("save_changes") : t("save_as_new_template")
        }
      />
      {!isDefaultTemplate && !PatientId && (
        <GeneralButton
          isLoading={UpdateMedicalTemplateMutation.isPending}
          type="submit"
          className="!h-fit !min-w-28 !px-5"
          onClick={() => setSaveOverOldTemplate(true)}
          title={t("save_changes")}
        />
      )}

      {PatientId && (
        <GeneralButton
          isLoading={createRecordMutation?.isPending}
          type="submit"
          className="!h-fit !min-w-28 !px-5"
          onClick={() => {
            setSaveTemporaryAndContinue(true);
            resetSteps();
          }}
          title={t("apply_changes_and_continue")}
        />
      )}
    </div>
  );

  useEffect(() => {
    if (localSteps.length > 0) {
      logFieldOrder(localSteps);
    }
  }, [localSteps]);

  //set state on form change

  useEffect(() => {
    if (!isEqual(prev.current, values) && !isCustomizeMode) {
      dispatch(setMedicalRecord({ ...values, PatientId: PatientId }));
      prev.current = values;
    }
  }, [values]);

  useEffect(() => {
    if (medicalRecord?.PatientId == PatientId && !isCustomizeMode) {
      medicalRecord && methods.reset(medicalRecord);
    }
  }, [isCustomizeMode]);

  if (type === "Preview")
    return (
      <PreviewMedicalRecord
        updateOrCreateRecord={updateOrCreateRecord}
        isLoading={createRecordMutation?.isPending}
      />
    );
  return (
    <div
      className="box mx-auto flex h-[100dvh] flex-col !pt-8"
      dir={i18next.language?.slice(0, 2) === "en" ? "ltr" : "rtl"}
    >
      <MedicalRecordHeader
        title={
          isViewMode
            ? t("view_medical_report_template")
            : t("add_medical_report")
        }
        type={!isCustomizeMode ? "add" : undefined}
        backRoute={
          PatientId ? `/patient/${PatientId}/add-medical-record` : undefined
        }
        selectedTemplateName={selectedTemplate?.Name}
      />
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(isCustomizeMode ? onSubmit : () => { })}
          className="mt-6 flex h-full flex-col"
        >
          {PatientId && <PatientInfo getMedicalTemplateQuery={getMedicalTemplateQuery} totalVisibleKeys={totalVisibleKeys} />}

          <div className="flex flex-1 gap-10">
            <MultiStepProgress
              prevStep={prevStep}
              Steps={localSteps}
              currentStep={currentStep}
              currentSubStep={currentSubStep}
              onSubStepClick={async (index: number) => {
                const fieldNames = getStepFieldNames(
                  currentSubStepData,
                  methods.getValues(),
                );
                const isValid = await trigger(fieldNames);

                if (!isValid) return;

                setCurrentSubStep((prev: number | undefined) => {
                  setPrevStep(prev as any);
                  return index;
                });
              }}
              onStepClick={async (index: number) => {
                const fieldNames = getStepFieldNames(
                  currentStepData,
                  methods.getValues(),
                );
                const isValid = await trigger(fieldNames);
                // const isValid = await trigger();

                if (!isValid) return;

                setCurrentStep((prev: number | undefined) => {
                  setPrevStep(prev as any);
                  return index;
                });
                setCurrentSubStep(0);
              }}
              setLocalSteps={setLocalSteps}
            />
            <div className="flex h-full w-full max-w-[69vw] flex-1 flex-col">
              {!isUpdateRecordMode && (
                <CustomizeButton resetSteps={resetSteps} />
              )}
              <div className="flex h-full w-full max-w-[69vw] flex-1 flex-col justify-between">
                <div className="medical-record grid h-[calc(100dvh-20rem)] w-full auto-rows-min grid-cols-1 gap-4 overflow-y-auto pt-2 lg:grid-cols-12">
                  {getMedicalTemplateQuery.isLoading ? (
                    LoadingFieldsSkeleton
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      {currentStepData?.StepFields?.length > 0 && (
                        <SortableContext
                          items={stepFieldIds}
                          strategy={verticalListSortingStrategy}
                        >
                          {currentStepData?.StepFields?.map(
                            (f: any, index: number) => {
                              return (
                                <SortableField
                                  key={`step-field-${index}`}
                                  field={f}
                                  isEditable={isCustomizeMode}
                                  isCurrentStepVisible={isCurrentStepVisible}
                                  currentStepData={currentStepData}
                                />
                              );
                            },
                          )}
                        </SortableContext>
                      )}

                      {currentSubStepData?.StepFields?.length > 0 && (
                        <SortableContext
                          items={subStepFieldIds}
                          strategy={verticalListSortingStrategy}
                        >
                          {currentSubStepData.StepFields.map(
                            (f: any, index: number) => (
                              <SortableSubStepField
                                key={`substep-field-${index}`}
                                field={f}
                                isEditable={isCustomizeMode}
                                currentStepData={currentStepData}
                                currentSubStepData={currentSubStepData}
                              />
                            ),
                          )}
                        </SortableContext>
                      )}

                      <DragOverlay>
                        {activeField ? (
                          <div className="opacity-50">
                            {activeField && (
                              <FieldRenderer
                                disabled={isViewMode}
                                field={activeField}
                              />
                            )}
                          </div>
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}
                </div>

                {isCustomizeMode ? customizeButtons : recordButtons}
              </div>
            </div>
          </div>
        </form>
      </FormProvider>

      {isSaveTemplateOpen && (
        <TemplateNameModal
          onClose={() => setIsSaveTemplateOpen(false)}
          isOpen={isSaveTemplateOpen}
          updatedTemplate={{
            ...updatedTemplate,
            MedicalHistoryTypeId: medicalRecordTemplate?.Id,
          }}
        />
      )}

      {isAskToSaveTemplateOpen && (
        <SaveRecordAndTemplateConfirm
          onClose={() => setIsAskToSaveTemplateOpen(false)}
          onSend={(TemplateId?: number) => {
            updateOrCreateRecord({
              ...recordFormData,
              MedicalHistoryTypeId: TemplateId,
            });
          }}
          isOpen={isAskToSaveTemplateOpen}
        />
      )}
    </div>
  );
};

export default MedicalRecord;
